import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Col, Form, FormProps, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { getAllStates } from '../../services/states';
import Button from '../Button';
import Radio, { RadioGroup } from '../Radio';
import Select from '../Select';
import TextInput from '../TextInput';

export type AddressType = 'home' | 'work' | 'alt';

export type Address = {
  type: AddressType;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
};

export type customAddressFormProps = FormProps &
  React.PropsWithoutRef<{
    existingAddress?: Address;
    handleClose: () => void;
    handleOnSubmit: (formData: any) => void;
  }>;

const newAddress: Address = {
  type: 'home',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
};

const AddressForm = ({
  existingAddress = newAddress,
  handleClose,
  handleOnSubmit,
  ...props
}: customAddressFormProps) => {
  const [address] = useState<Address>(existingAddress);

  const isEditForm = address['address1'] !== '';

  const validationSchema = yup.object().shape({
    type: yup.string().required('Type is required'),
    address1: yup
      .string()
      .required('Address 1 is required')
      .max(245, 'Must be no longer than 245 characters'),
    address2: yup.string(),
    city: yup
      .string()
      .required('City is required')
      .max(245, 'Must be no longer than 245 characters'),
    state: yup
      .string()
      .required('State is required')
      .max(245, 'Must be no longer than 245 characters'),
    zipCode: yup.string().required('Zip Code is required'),
  });

  const statesOptions = getAllStates();

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: address,
    resolver: yupResolver(validationSchema),
  });

  const handleSubmitForm = (formData: any) => {
    handleOnSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)} noValidate={true}>
      <h4 className="mb-12 text-sm-20-lg-32">
        {isEditForm ? 'Edit Address' : 'Add New Address'}
      </h4>
      {!isEditForm && (
        <>
          <Form.Label className="text-fs-6">Type of Address</Form.Label>
          <Controller
            name={'type'}
            control={control}
            render={({ field }) => (
              <RadioGroup {...field}>
                <Radio
                  value="home"
                  id="address-type-home"
                  label="Home"
                  className="mt-6 text-sm-12-lg-14"
                />
                <Radio
                  value="work"
                  id="address-type-work"
                  label="Work"
                  className="mt-6"
                />
                <Radio
                  value="alt"
                  id="address-type-alt"
                  label="Alternative"
                  className="mt-6"
                />
              </RadioGroup>
            )}
          />
        </>
      )}
      <Controller
        name={'address1'}
        control={control}
        render={({ field }) => (
          <TextInput
            leftIcon={light('location-pin')}
            label="Address 1"
            className="mt-8"
            placeholder="Address"
            isInvalid={!!errors.address1}
            {...field}
          />
        )}
      />
      {errors.address1 && (
        <div className="error-message">
          <span>{errors.address1.message}</span>
        </div>
      )}
      <Controller
        name={'address2'}
        control={control}
        render={({ field }) => (
          <TextInput
            leftIcon={light('location-pin')}
            label="Address 2 (Optional)"
            className="mt-8"
            placeholder="Address"
            {...field}
          />
        )}
      />
      <Row className="mt-8">
        <Col className="col-12 col-lg-3">
          <Controller
            name={'city'}
            control={control}
            render={({ field }) => (
              <TextInput label="City" isInvalid={!!errors.city} {...field} />
            )}
          />
          {errors.city && (
            <div className="error-message">
              <span>{errors.city.message}</span>
            </div>
          )}
        </Col>
        <Col className="mt-4 mt-lg-0 col-12 col-lg-6">
          <Controller
            name={'state'}
            control={control}
            render={({ field }) => (
              <Select
                label="State"
                options={statesOptions}
                {...field}
                isInvalid={!!errors.state}
              />
            )}
          />

          {errors.state && (
            <div className="error-message">
              <span>{errors.state.message}</span>
            </div>
          )}
        </Col>
        <Col className="mt-4 mt-lg-0 col-12 col-lg-3">
          <Controller
            name={'zipCode'}
            control={control}
            render={({ field }) => (
              <TextInput
                label="ZIP Code"
                {...field}
                isInvalid={!!errors.zipCode}
                maxLength={5}
              />
            )}
          />

          {errors.zipCode && (
            <div className="error-message">
              <span>{errors.zipCode.message}</span>
            </div>
          )}
        </Col>
      </Row>
      <section className="py-11 py-lg-12 d-flex justify-content-center flex-column flex-lg-row">
        <Button
          className="mt-7 mt-lg-0 me-lg-7 order-2 order-lg-1"
          variant="primary-outline"
          onMouseDown={handleClose}
        >
          Cancel
        </Button>
        <Button variant="primary" className="order-1 order-lg-2" type="submit">
          Save
        </Button>
      </section>
    </form>
  );
};

export default AddressForm;
